import React from 'react';
import type { PersonnelDetail } from '@/types/personnelDetail';
import styles from './AdditionalPersonnelInfo.module.css';

interface AdditionalPersonnelInfoProps {
  data: PersonnelDetail;
}

type DetailKey = keyof Pick<PersonnelDetail,
  | 'personalEmail' | 'maritalStatus' | 'militaryService' | 'permanentWard' | 'currentWard'
  | 'idNumber' | 'idIssuePlace' | 'idIssueDate' | 'idExpiryDate' | 'passportType'
  | 'passportNumber' | 'passportIssuePlace' | 'passportIssueDate' | 'passportExpiryDate'
  | 'officialContractDate' | 'directManager' | 'laborType' | 'referencePerson'
>;

const groups: Array<{ title: string; fields: Array<{ key: DetailKey; label: string }> }> = [
  {
    title: 'Thông tin cá nhân và liên hệ',
    fields: [
      { key: 'personalEmail', label: 'Email cá nhân' },
      { key: 'maritalStatus', label: 'Tình trạng hôn nhân' },
      { key: 'militaryService', label: 'Nghĩa vụ quân sự' },
      { key: 'permanentWard', label: 'Phường/xã thường trú' },
      { key: 'currentWard', label: 'Phường/xã hiện tại' },
    ],
  },
  {
    title: 'Thông tin giấy tờ',
    fields: [
      { key: 'idNumber', label: 'Số CC/CCCD/CMT' },
      { key: 'idIssuePlace', label: 'Nơi cấp' },
      { key: 'idIssueDate', label: 'Ngày cấp' },
      { key: 'idExpiryDate', label: 'Ngày hết hạn' },
      { key: 'passportType', label: 'Loại hộ chiếu' },
      { key: 'passportNumber', label: 'Số hộ chiếu' },
      { key: 'passportIssuePlace', label: 'Nơi cấp hộ chiếu' },
      { key: 'passportIssueDate', label: 'Ngày cấp hộ chiếu' },
      { key: 'passportExpiryDate', label: 'Ngày hết hạn hộ chiếu' },
    ],
  },
  {
    title: 'Thông tin công việc bổ sung',
    fields: [
      { key: 'officialContractDate', label: 'Ngày ký hợp đồng' },
      { key: 'directManager', label: 'Quản lý trực tiếp' },
      { key: 'laborType', label: 'Loại lao động' },
      { key: 'referencePerson', label: 'Người tham chiếu' },
    ],
  },
];

const displayValues: Record<string, string> = {
  single: 'Độc thân', married: 'Đã kết hôn', divorced: 'Ly hôn', widowed: 'Góa',
  yes: 'Đã hoàn thành', no: 'Hoãn nghĩa vụ', exempt: 'Miễn nghĩa vụ',
  'full-time': 'Toàn thời gian', fulltime: 'Toàn thời gian',
  'part-time': 'Bán thời gian', parttime: 'Bán thời gian',
  contract: 'Hợp đồng', intern: 'Thực tập', internship: 'Thực tập',
};

export const AdditionalPersonnelInfo: React.FC<AdditionalPersonnelInfoProps> = ({ data }) => {
  const visibleGroups = groups.map((group) => ({
    ...group,
    fields: group.fields.filter(({ key }) => String(data[key] ?? '').trim() !== ''),
  })).filter((group) => group.fields.length > 0);

  if (visibleGroups.length === 0) return null;

  return (
    <div className={styles.groups}>
      {visibleGroups.map((group) => (
        <section className={styles.group} key={group.title}>
          <h2 className={styles.heading}>{group.title}</h2>
          <dl className={styles.grid}>
            {group.fields.map(({ key, label }) => {
              const value = String(data[key]);
              return (
                <div className={styles.field} key={key}>
                  <dt>{label}</dt>
                  <dd>{displayValues[value.toLowerCase()] || value}</dd>
                </div>
              );
            })}
          </dl>
        </section>
      ))}
    </div>
  );
};
